import { supabase } from '../client';
import { TeeBox, toManualUserId } from '@/types';
import { getGameScoreIds } from './scoreGameService';

export interface GolferBirdie {
  userId: string;
  name: string;
  imageUrl: string | null;
  bestScoreType: 'birdie' | 'eagle' | 'double_eagle';
}

export interface HoleTileData {
  holeNumber: number;
  golferBirdies: GolferBirdie[];
  bestScoreType?: 'birdie' | 'eagle' | 'double_eagle';
  teeBoxBirdies: { teeBox: TeeBox; count: number }[];
}

const SCORE_TYPE_RANK: Record<string, number> = {
  double_eagle: 3,
  eagle: 2,
  birdie: 1,
};

export function betterScoreType(
  a?: 'birdie' | 'eagle' | 'double_eagle',
  b?: 'birdie' | 'eagle' | 'double_eagle'
): 'birdie' | 'eagle' | 'double_eagle' | undefined {
  if (!a) return b;
  if (!b) return a;
  return (SCORE_TYPE_RANK[a] >= SCORE_TYPE_RANK[b]) ? a : b;
}

/**
 * Fetches all tile display data for a game's holes, including per-golfer
 * birdie info (for avatar display) with profile joins.
 *
 * @param courseId - the course to fetch scores for
 * @param gameId - the game to scope scores to (via score_games)
 */
export async function fetchHoleTileData(params: {
  courseId: string;
  gameId: string;
}): Promise<Map<number, HoleTileData>> {
  const { courseId, gameId } = params;
  const result = new Map<number, HoleTileData>();

  const scoreIds = await getGameScoreIds(gameId);
  if (scoreIds.length === 0) return result;

  const { data: scores, error } = await supabase
    .from('scores')
    .select('hole_number, tee, score_type, user_id, manual_player_id, profiles:user_id(id, name, image_url), manual_players:manual_player_id(id, name)')
    .eq('course_id', courseId)
    .in('id', scoreIds);

  if (error) {
    console.error('Error fetching hole tile data:', error);
    return result;
  }

  if (!scores) return result;

  for (const score of scores) {
    const hn = score.hole_number;
    let tile = result.get(hn);
    if (!tile) {
      tile = {
        holeNumber: hn,
        golferBirdies: [],
        bestScoreType: undefined,
        teeBoxBirdies: [],
      };
      result.set(hn, tile);
    }

    const scoreType = (score.score_type as 'birdie' | 'eagle' | 'double_eagle') || 'birdie';
    const manualPlayer = score.manual_players as { id: string; name: string } | null;
    const profile = score.profiles as { id: string; name: string | null; image_url: string | null } | null;

    // Determine the effective golfer: manual player takes priority
    const effectiveUserId = manualPlayer ? toManualUserId(manualPlayer.id) : score.user_id;
    const effectiveName = manualPlayer ? manualPlayer.name : (profile?.name || 'Unknown');
    const effectiveImageUrl = manualPlayer ? null : (profile?.image_url || null);

    // Aggregate per-golfer: deduplicate by userId, keep best score type
    const existingGolfer = tile.golferBirdies.find(g => g.userId === effectiveUserId);
    if (existingGolfer) {
      existingGolfer.bestScoreType = betterScoreType(existingGolfer.bestScoreType, scoreType) || 'birdie';
    } else {
      tile.golferBirdies.push({
        userId: effectiveUserId,
        name: effectiveName,
        imageUrl: effectiveImageUrl,
        bestScoreType: scoreType,
      });
    }

    // Overall best score type for the hole
    tile.bestScoreType = betterScoreType(tile.bestScoreType, scoreType);

    // Tee box aggregation
    const tee = score.tee as TeeBox;
    const existingTee = tile.teeBoxBirdies.find(t => t.teeBox === tee);
    if (existingTee) {
      existingTee.count += 1;
    } else {
      tile.teeBoxBirdies.push({ teeBox: tee, count: 1 });
    }
  }

  return result;
}

/**
 * Fetches aggregate tile data across multiple game instances (for all-time view).
 */
export async function fetchAllTimeHoleTileData(params: {
  courseId: string;
  gameIds: string[];
}): Promise<Map<number, HoleTileData>> {
  const { courseId, gameIds } = params;
  const result = new Map<number, HoleTileData>();

  // Collect all score IDs across all game instances
  const allScoreIds: string[] = [];
  for (const gid of gameIds) {
    const ids = await getGameScoreIds(gid);
    allScoreIds.push(...ids);
  }

  // Deduplicate score IDs (a score could theoretically be linked to multiple game instances)
  const uniqueScoreIds = [...new Set(allScoreIds)];
  if (uniqueScoreIds.length === 0) return result;

  const { data: scores, error } = await supabase
    .from('scores')
    .select('hole_number, tee, score_type, user_id, manual_player_id, profiles:user_id(id, name, image_url), manual_players:manual_player_id(id, name)')
    .eq('course_id', courseId)
    .in('id', uniqueScoreIds);

  if (error) {
    console.error('Error fetching all-time hole tile data:', error);
    return result;
  }

  if (!scores) return result;

  for (const score of scores) {
    const hn = score.hole_number;
    let tile = result.get(hn);
    if (!tile) {
      tile = {
        holeNumber: hn,
        golferBirdies: [],
        bestScoreType: undefined,
        teeBoxBirdies: [],
      };
      result.set(hn, tile);
    }

    const scoreType = (score.score_type as 'birdie' | 'eagle' | 'double_eagle') || 'birdie';
    const manualPlayer = score.manual_players as { id: string; name: string } | null;
    const profile = score.profiles as { id: string; name: string | null; image_url: string | null } | null;

    const effectiveUserId = manualPlayer ? toManualUserId(manualPlayer.id) : score.user_id;
    const effectiveName = manualPlayer ? manualPlayer.name : (profile?.name || 'Unknown');
    const effectiveImageUrl = manualPlayer ? null : (profile?.image_url || null);

    const existingGolfer = tile.golferBirdies.find(g => g.userId === effectiveUserId);
    if (existingGolfer) {
      existingGolfer.bestScoreType = betterScoreType(existingGolfer.bestScoreType, scoreType) || 'birdie';
    } else {
      tile.golferBirdies.push({
        userId: effectiveUserId,
        name: effectiveName,
        imageUrl: effectiveImageUrl,
        bestScoreType: scoreType,
      });
    }

    tile.bestScoreType = betterScoreType(tile.bestScoreType, scoreType);

    const tee = score.tee as TeeBox;
    const existingTee = tile.teeBoxBirdies.find(t => t.teeBox === tee);
    if (existingTee) {
      existingTee.count += 1;
    } else {
      tile.teeBoxBirdies.push({ teeBox: tee, count: 1 });
    }
  }

  return result;
}
